import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { updateInstallOnboarding } from "../store/slices/installSlice";
import { postOnboardingStatus } from "../api/clientOnboarding";
import { fetchTemplates } from "../api/templatesApi";

/**
 * DB is_on_boarding; step 2 also from theme GraphQL.
 * - 1: at least one template exists (auto-sync when API returns templates)
 * - 2: theme embed active (auto-sync only after step 1, when GraphQL says enabled)
 * - 3: user marked setup complete (guide may still show)
 * - 4: setup guide dismissed (any window — stored in DB)
 * - 5: same as 3 + guide dismissed
 *
 * Setup guide step 1 (template) is driven only by hasTemplates — not by numeric status alone,
 * so status 2 with zero templates does not show step 1 complete.
 */
export function useAgeShieldOnboarding({
  installChecked,
  installData,
  themeLoading,
  embedInactive,
}) {
  const dispatch = useDispatch();
  const onboardingFromStore = installData?.shop_info?.is_on_boarding ?? "0";
  const clientId = installData?.shop_info?.id;
  const backendStatus = parseInt(String(onboardingFromStore), 10);
  const safeBackend = Number.isFinite(backendStatus) ? backendStatus : 0;

  const setupGuideDismissed = safeBackend === 4 || safeBackend === 5;

  const installReadyRef = useRef(false);
  useEffect(() => {
    if (installChecked && installData) {
      installReadyRef.current = true;
    }
  }, [installChecked, installData]);

  const [templatesChecked, setTemplatesChecked] = useState(false);
  const [hasTemplates, setHasTemplates] = useState(false);

  useEffect(() => {
    const cid = installData?.shop_info?.id;
    if (!installChecked || !cid) {
      setTemplatesChecked(false);
      setHasTemplates(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTemplates();
        const list = res?.data?.templates ?? [];
        if (!cancelled) {
          setHasTemplates(Array.isArray(list) && list.length > 0);
          setTemplatesChecked(true);
        }
      } catch (e) {
        console.warn("[useAgeShieldOnboarding] templates fetch:", e);
        if (!cancelled) {
          setHasTemplates(false);
          setTemplatesChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [installChecked, installData?.shop_info?.id]);

  const applyStatus = useCallback(
    (status) => {
      dispatch(updateInstallOnboarding(String(status)));
    },
    [dispatch],
  );

  const persist = useCallback(
    async (status) => {
      if (!clientId) {
        return { success: false, message: "No client" };
      }
      try {
        const res = await postOnboardingStatus(status);
        if (res?.success) {
          applyStatus(status);
          return { success: true };
        }
        return { success: false, message: res?.message };
      } catch (e) {
        console.error("[useAgeShieldOnboarding] persist:", e);
        return { success: false };
      }
    },
    [clientId, applyStatus],
  );

  /**
   * Sync DB with real state (linear): templates exist → at least 1; embed on → at least 2 only after templates.
   * Embed active alone must not bump status to 2 — otherwise the setup guide marks step 1 done without a template.
   * Skip when status is 3+ so we never overwrite 4/5 (dismissed) or completed 3.
   */
  useEffect(() => {
    if (!installReadyRef.current || !clientId || themeLoading || !templatesChecked) return;
    if (safeBackend === 3 || safeBackend === 4 || safeBackend === 5) return;

    if (hasTemplates && safeBackend < 1) {
      let cancelled = false;
      (async () => {
        const r = await persist("1");
        if (!cancelled && !r.success) {
          console.warn("[useAgeShieldOnboarding] Could not sync step 1 to DB");
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (!embedInactive && hasTemplates && safeBackend < 2) {
      let cancelled = false;
      (async () => {
        const r = await persist("2");
        if (!cancelled && !r.success) {
          console.warn("[useAgeShieldOnboarding] Could not sync step 2 to DB");
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [
    embedInactive,
    themeLoading,
    clientId,
    safeBackend,
    persist,
    templatesChecked,
    hasTemplates,
  ]);

  const markTemplateStep = useCallback(async () => {
    if (safeBackend === 4 || safeBackend === 5) {
      return { success: true };
    }
    if (!hasTemplates) {
      return { success: false, message: "Create a template in Settings first." };
    }
    if (safeBackend >= 1) {
      return { success: true };
    }
    return persist("1");
  }, [safeBackend, hasTemplates, persist]);

  const markSetupComplete = useCallback(async () => {
    if (safeBackend === 3 || safeBackend === 5) {
      return { success: true };
    }
    if (!hasTemplates) {
      return { success: false, message: "Create a template in Settings first." };
    }
    if (embedInactive) {
      return { success: false, message: "Turn on the theme app embed first." };
    }
    if (safeBackend === 4) {
      return persist("5");
    }
    return persist("3");
  }, [safeBackend, hasTemplates, embedInactive, persist]);

  const dismissSetupGuide = useCallback(async () => {
    if (safeBackend === 4 || safeBackend === 5) {
      return { success: true };
    }
    const next = safeBackend >= 3 ? "5" : "4";
    return persist(next);
  }, [safeBackend, persist]);

  /** Step 1 = template exists. DB status 2 alone does not mean step 1 if the store has zero templates. */
  const step1Done = hasTemplates;
  const step2Done = !embedInactive;
  const step3Done = safeBackend === 3 || safeBackend === 5;

  const completedCount = [step1Done, step2Done, step3Done].filter(Boolean).length;

  return {
    onboardingStatus: onboardingFromStore,
    setupGuideDismissed,
    templatesChecked,
    step1Done,
    step2Done,
    step3Done,
    completedCount,
    markTemplateStep,
    markSetupComplete,
    dismissSetupGuide,
    persist,
  };
}
